import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

const AssignmentContainer = styled.div`
  margin: 12px 0;
  padding: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f9fafb;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 6px;
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const UserSelect = styled.select`
  width: 100%;
  padding: 8px 12px;
  border: 2px solid #e5e7eb;
  border-radius: 6px;
  background: white;
  font-size: 14px;
  color: #374151;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
  
  &:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const UserOption = styled.option`
  padding: 8px;
`;

const LoadingText = styled.div`
  color: #6b7280;
  font-size: 14px;
  font-style: italic;
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 14px;
`;

const AssignedUserDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 0;
`;

const UserAvatar = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  object-fit: cover;
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-weight: 500;
  color: #374151;
  font-size: 14px;
`;

const UserEmail = styled.span`
  color: #6b7280;
  font-size: 12px;
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
  padding: 2px 4px;
  border-radius: 4px;
  margin-left: auto;
  
  &:hover {
    background: #fef2f2;
  }
`;

const UserAssignment = ({ 
  assignedUserId, 
  onAssign, 
  disabled = false,
  className 
}) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [assignedUser, setAssignedUser] = useState(null);

  // Debug logging
  console.log('UserAssignment component rendered with:', { assignedUserId, disabled, usersCount: users.length });

  // Load users when component mounts
  useEffect(() => {
    loadUsers();
  }, []);

  // Load assigned user info when assignedUserId changes
  useEffect(() => {
    if (assignedUserId && users.length > 0) {
      const user = users.find(u => u.id === assignedUserId);
      setAssignedUser(user);
    } else {
      setAssignedUser(null);
    }
  }, [assignedUserId, users]);

  const loadUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch users from our API endpoint
      const response = await fetch('/api/get-users.php', {
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        // If PHP endpoint is not available (dev mode), use mock users
        console.warn('PHP users endpoint not available, using mock users for development');
        setUsers([
          {
            id: 'mock-user-1',
            firstName: 'John',
            lastName: 'Doe',
            username: 'johndoe',
            emailAddresses: [{ emailAddress: 'john@example.com' }],
            profileImageUrl: null
          },
          {
            id: 'mock-user-2',
            firstName: 'Jane',
            lastName: 'Smith',
            username: 'janesmith',
            emailAddresses: [{ emailAddress: 'jane@example.com' }],
            profileImageUrl: null
          }
        ]);
        setLoading(false);
        return;
      }
      
      const userData = await response.json();
      setUsers(userData.users || []);
    } catch (err) {
      console.error('Error loading users:', err);
      console.warn('Falling back to mock users for development');
      
      // Fallback to mock users for development
      setUsers([
        {
          id: 'mock-user-1',
          firstName: 'John',
          lastName: 'Doe',
          username: 'johndoe',
          emailAddresses: [{ emailAddress: 'john@example.com' }],
          profileImageUrl: null
        },
        {
          id: 'mock-user-2',
          firstName: 'Jane',
          lastName: 'Smith',
          username: 'janesmith',
          emailAddresses: [{ emailAddress: 'jane@example.com' }],
          profileImageUrl: null
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleAssignUser = (userId) => {
    if (onAssign) {
      onAssign(userId === 'unassigned' ? null : userId);
    }
  };

  const handleRemoveAssignment = () => {
    if (onAssign) {
      onAssign(null);
    }
  };

  if (assignedUser) {
    return (
      <AssignmentContainer className={className}>
        <Label>Assigned To:</Label>
        <AssignedUserDisplay>
          {assignedUser.profileImageUrl && (
            <UserAvatar 
              src={assignedUser.profileImageUrl} 
              alt={assignedUser.firstName || assignedUser.username}
            />
          )}
          <UserInfo>
            <UserName>
              {assignedUser.firstName && assignedUser.lastName 
                ? `${assignedUser.firstName} ${assignedUser.lastName}`
                : assignedUser.username || assignedUser.emailAddresses?.[0]?.emailAddress}
            </UserName>
            {assignedUser.emailAddresses?.[0]?.emailAddress && (
              <UserEmail>{assignedUser.emailAddresses[0].emailAddress}</UserEmail>
            )}
          </UserInfo>
          {!disabled && (
            <RemoveButton 
              onClick={handleRemoveAssignment}
              title="Remove assignment"
            >
              ✕
            </RemoveButton>
          )}
        </AssignedUserDisplay>
      </AssignmentContainer>
    );
  }

  return (
    <AssignmentContainer className={className}>
      <Label>Assign To:</Label>
      {loading && <LoadingText>Loading users...</LoadingText>}
      {error && <ErrorText>{error}</ErrorText>}
      {!loading && !error && (
        <UserSelect
          value={assignedUserId || 'unassigned'}
          onChange={(e) => handleAssignUser(e.target.value)}
          disabled={disabled}
        >
          <UserOption value="unassigned">Unassigned</UserOption>
          {users.map(user => (
            <UserOption key={user.id} value={user.id}>
              {user.firstName && user.lastName 
                ? `${user.firstName} ${user.lastName}`
                : user.username || user.emailAddresses?.[0]?.emailAddress || user.id}
            </UserOption>
          ))}
        </UserSelect>
      )}
    </AssignmentContainer>
  );
};

export default UserAssignment; 